import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import VueExcelXlsx from "vue-excel-xlsx"
import { createBootstrap } from 'bootstrap-vue-next'
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'

// Import des composants Bootstrap explicitement
import { 
  BModal, BButton, BTable, BDropdown, BDropdownItem, 
  BPagination, BFormCheckbox, BFormCheckboxGroup, 
  BSpinner, BFormInput, BFormTextarea, BFormSelect, BFormGroup,
  BCard, BCardBody, BCardTitle, BRow, BCol,
  BNavbar, BNavbarNav, BNavItem, BNavbarBrand,
  BContainer, BForm, BFormFile, BAlert, BBadge,
  BInputGroup, BInputGroupText, BFormRadio, BFormRadioGroup
} from 'bootstrap-vue-next'

// Import VueDatePicker pour les sélecteurs de date/heure
import VueDatePicker from '@vuepic/vue-datepicker';

import DateTimeService  from "./services/datetime.service.js";
import { createHead } from 'unhead'

// Localisation
import { createI18n } from 'vue-i18n'
import { ENGLISH_TRANSLATIONS } from './assets/localisation/en';
import { FRENCH_TRANSLATIONS } from './assets/localisation/fr';

import '../public/style/bootstrap.key4.css';
import '../public/style/font-awesome.key4.css';
import '../public/style/key4.css';
import 'v-calendar/style.css';
import '@vuepic/vue-datepicker/dist/main.css'

//#region FontAwesome
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faUserCircle, faSearch, faPlus, faPowerOff, faTachometerAlt, faHome, faCalendar, faQuestionCircle, faUsers, faTrashAlt, faPen, faSquare, faCheckSquare, faPlay, faStop, faTasks, faSignOutAlt, faUser, faAngleDown, faHistory, faTimes, faInfoCircle, faAddressBook, faChartPie, faChartBar, faClipboardCheck, faChevronLeft, faChevronRight, faFileExcel, faSyncAlt, faMinusCircle, faMinus, faFilePdf, faRobot, faList, faAddressCard, faTriangleExclamation, faFileCircleCheck, faFileCircleXmark, faLocationDot, faIdBadge, faUserCheck, faGear, faUpload, faQrcode, faUserGroup, faTowerBroadcast, faCircleLeft, faEye, faEyeSlash, faBars, faArrowLeft, faLanguage, faBook, faDownload, faBoxArchive, faRotate } from '@fortawesome/free-solid-svg-icons'

library.add(faUserCircle, faPowerOff, faTachometerAlt, faHome, faCalendar, faQuestionCircle, faUsers, faSearch, faPlus, faTrashAlt, faPen, faSquare, faCheckSquare, faPlay, faStop, faTasks, faSignOutAlt, faUser, faAngleDown, faHistory, faTimes, faInfoCircle, faAddressBook, faChartPie, faChartBar, faClipboardCheck, faChevronLeft, faChevronRight, faFileExcel, faSyncAlt, faMinusCircle, faMinus, faFilePdf, faRobot, faList, faAddressCard, faTriangleExclamation, faFileCircleCheck, faFileCircleXmark, faLocationDot, faIdBadge, faUserCheck, faGear, faUpload, faQrcode, faUserGroup, faTowerBroadcast, faCircleLeft, faEye, faEyeSlash, faBars, faArrowLeft, faLanguage, faBook, faDownload, faBoxArchive, faRotate);
//#endregion

// ===== Vérification de version pour forcer le refresh =====
const checkAppVersion = async () => {
  try {
    const response = await fetch('/version.json?t=' + Date.now());
    const data = await response.json();
    
    const storedVersion = localStorage.getItem('appVersion');
    const storedBuildTime = localStorage.getItem('appBuildTime');
    
    // Si une version est déjà stockée et qu'elle diffère, recharger la page
    if (storedVersion && storedBuildTime) {
      if (storedVersion !== data.version || storedBuildTime !== data.buildTime) {
        console.log('🔄 Nouvelle version détectée:', data.version, 'Build:', data.buildTime);
        console.log('📦 Ancienne version:', storedVersion, 'Build:', storedBuildTime);
        
        // Stocker la nouvelle version
        localStorage.setItem('appVersion', data.version);
        localStorage.setItem('appBuildTime', data.buildTime);
        
        // Recharger la page pour obtenir la nouvelle version
        console.log('🔃 Rechargement de l\'application...');
        window.location.reload(true);
        return;
      }
    } else {
      // Première visite, stocker la version
      localStorage.setItem('appVersion', data.version);
      localStorage.setItem('appBuildTime', data.buildTime);
      console.log('✓ Version de l\'application:', data.version);
    }
  } catch (error) {
    console.error('⚠️ Erreur lors de la vérification de version:', error);
  }
};

// Vérifier la version au démarrage de l'application
checkAppVersion();

// Vérifier périodiquement (toutes les 30 minutes)
setInterval(checkAppVersion, 30 * 60 * 1000);

// ===== Configuring instances =====
// Create i18n instance with options
const TRANSLATIONS = {
  en: ENGLISH_TRANSLATIONS,
  fr: FRENCH_TRANSLATIONS
};
const i18n = createI18n({
  locale: 'en',
  legacy: false,
  messages: TRANSLATIONS,
});

// Adding custom JS files as plugins so we can use global functions within the app
const plugins = {
  install(){
    app.DateTimeService = DateTimeService;
    app.config.globalProperties.$DateTimeService = DateTimeService;
    
    // Compatibility layer for $bvToast
    app.config.globalProperties.$bvToast = {
      toast: () => {
        // Using browser's native notification as a simple fallback
        // In production, you might want to implement a proper toast system
        // You can implement a proper toast UI here or use a toast library
      }
    };
  }
}

// ===== Create a vue root instance =====
export const app = createApp(App);
createHead();

// ===== Configuring app-scoped assets =====
app.component('font-awesome-icon', FontAwesomeIcon);

// Enregistrer les composants Bootstrap explicitement
app.component('BModal', BModal);
app.component('BButton', BButton);
app.component('BTable', BTable);
app.component('BDropdown', BDropdown);
app.component('BDropdownItem', BDropdownItem);
app.component('BPagination', BPagination);
app.component('BFormCheckbox', BFormCheckbox);
app.component('BFormCheckboxGroup', BFormCheckboxGroup);
app.component('BSpinner', BSpinner);
app.component('VueDatePicker', VueDatePicker);
app.component('BFormInput', BFormInput);
app.component('BFormTextarea', BFormTextarea);
app.component('BFormSelect', BFormSelect);
app.component('BFormGroup', BFormGroup);
app.component('BCard', BCard);
app.component('BCardBody', BCardBody);
app.component('BCardTitle', BCardTitle);
app.component('BRow', BRow);
app.component('BCol', BCol);
app.component('BNavbar', BNavbar);
app.component('BNavbarNav', BNavbarNav);
app.component('BNavItem', BNavItem);
app.component('BNavbarBrand', BNavbarBrand);
app.component('BContainer', BContainer);
app.component('BForm', BForm);
app.component('BFormFile', BFormFile);
app.component('BAlert', BAlert);
app.component('BBadge', BBadge);
app.component('BInputGroup', BInputGroup);
app.component('BInputGroupText', BInputGroupText);
app.component('BFormRadio', BFormRadio);
app.component('BFormRadioGroup', BFormRadioGroup);

// ===== Install instances to make the whole app aware of usable libraries =====
app.use(store);
app.use(router);
app.use(createBootstrap());
app.use(i18n);
app.use(VueExcelXlsx);
app.use(plugins);

app.config.globalProperties.$apiBaseUrl = 'TEST ME'

// ===== Mount =====
app.mount('#app');
